import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {Object} BundleBuilderCartStarterConfirmRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} starterTitle
 * @property {HTMLButtonElement} confirmButton
 */

export class BundleBuilderCartStarterConfirmDialog extends DialogComponent {
  /** @type {(() => void) | null} */
  #onConfirm = null;

  /** @type {HTMLElement | null} */
  #triggerElement = null;

  /** @type {(() => void) | null} */
  #dismissHandler = null;

  /**
   * @param {string} starterTitle
   * @param {() => void} onConfirm
   * @param {HTMLElement | null} [triggerElement]
   */
  prompt(starterTitle, onConfirm, triggerElement = null) {
    this.#onConfirm = onConfirm;
    this.#triggerElement = triggerElement;

    const { starterTitle: starterTitleRef } = this.refs;
    if (starterTitleRef instanceof HTMLElement) {
      starterTitleRef.textContent = starterTitle;
    }

    this.#bindDismissHandler();
    this.showDialog();

    requestAnimationFrame(() => {
      const { confirmButton } = this.refs;
      if (confirmButton instanceof HTMLButtonElement) {
        confirmButton.focus();
      }
    });
  }

  #bindDismissHandler() {
    this.#unbindDismissHandler();

    this.#dismissHandler = () => {
      this.#onConfirm = null;
      this.#restoreTriggerFocus();
      this.#dismissHandler = null;
    };

    this.addEventListener(DialogCloseEvent.eventName, this.#dismissHandler, { once: true });
  }

  #unbindDismissHandler() {
    if (!this.#dismissHandler) return;

    this.removeEventListener(DialogCloseEvent.eventName, this.#dismissHandler);
    this.#dismissHandler = null;
  }

  handleConfirm = async () => {
    const onConfirm = this.#onConfirm;
    const trigger = this.#triggerElement;
    this.#onConfirm = null;
    this.#triggerElement = null;
    this.#unbindDismissHandler();

    await this.closeDialog();
    onConfirm?.();

    if (trigger && document.contains(trigger)) {
      trigger.focus();
    }
  };

  handleCancel = async () => {
    this.#onConfirm = null;
    this.#unbindDismissHandler();
    await this.closeDialog();
    this.#restoreTriggerFocus();
  };

  #restoreTriggerFocus() {
    if (this.#triggerElement && document.contains(this.#triggerElement)) {
      this.#triggerElement.focus();
    }
    this.#triggerElement = null;
  }
}

if (!customElements.get('bundle-builder-cart-starter-confirm-dialog')) {
  customElements.define('bundle-builder-cart-starter-confirm-dialog', BundleBuilderCartStarterConfirmDialog);
}
